import { COMPETITOR_STATUSES, type CompetitorStatus, PARTICIPANTS_PAGE_SIZE_DEFAULT } from "@seagames/contracts";
import { z } from "zod";
import { cleanText, idParam, pageParam, sizeParam, textParam } from "../nocs/list-params";

/**
 * Teams & entries: the filters live in the URL
 * (`/teams?q&sport&discipline&event&noc&status&page&size`), like the other
 * lists. Discipline and event are only meaningful with their sport.
 */
export const teamsSearch = z.object({
	/** Team name, a member's printed name, the NOC's name or code. */
	q: textParam,
	sport: idParam,
	discipline: idParam,
	event: idParam,
	noc: idParam,
	status: z.enum(COMPETITOR_STATUSES).optional().catch(undefined),
	page: pageParam,
	size: sizeParam,
});
export type TeamsSearch = z.infer<typeof teamsSearch>;

export interface TeamsDraft extends Record<string, unknown> {
	q: string;
	sport: string;
	discipline: string;
	event: string;
	noc: string;
	status: CompetitorStatus | "";
}

export const EMPTY_TEAMS_DRAFT: TeamsDraft = { q: "", sport: "", discipline: "", event: "", noc: "", status: "" };

export const teamsDraftFrom = (s: TeamsSearch): TeamsDraft => ({
	q: s.q ?? "",
	sport: s.sport ?? "",
	discipline: s.sport ? (s.discipline ?? "") : "",
	event: s.sport ? (s.event ?? "") : "",
	noc: s.noc ?? "",
	status: s.status ?? "",
});

/** Draft → URL: empties dropped, discipline and event only with their sport, the page starts over. */
export function teamsSearchFrom(d: TeamsDraft, keep: Pick<TeamsSearch, "size"> = {}): TeamsSearch {
	return {
		q: cleanText(d.q),
		sport: d.sport || undefined,
		discipline: d.sport && d.discipline ? d.discipline : undefined,
		event: d.sport && d.event ? d.event : undefined,
		noc: d.noc || undefined,
		status: d.status || undefined,
		size: keep.size,
	};
}

export const teamsFilterCount = (s: TeamsSearch): number =>
	[s.q, s.sport, s.sport ? s.discipline : undefined, s.sport ? s.event : undefined, s.noc, s.status].filter((v) => v !== undefined).length;

export const teamsPaging = (s: TeamsSearch) => ({ page: s.page ?? 1, pageSize: s.size ?? PARTICIPANTS_PAGE_SIZE_DEFAULT });

/** The `GET /competitors` query for one games. */
export interface CompetitorsParams {
	games_id: string;
	sport_id?: string;
	discipline_id?: string;
	event_id?: string;
	responsible_organization_id?: string;
	status?: CompetitorStatus;
	search?: string;
	page: number;
	page_size: number;
}

export function teamsParams(gamesId: string, s: TeamsSearch): CompetitorsParams {
	const { page, pageSize } = teamsPaging(s);
	return {
		games_id: gamesId,
		sport_id: s.sport,
		discipline_id: s.sport ? s.discipline : undefined,
		event_id: s.sport ? s.event : undefined,
		responsible_organization_id: s.noc,
		status: s.status,
		search: s.q,
		page,
		page_size: pageSize,
	};
}
